/**
 * ScheduledReportsTab — Phase 5.6 UI.
 *
 * Managers+ only: list/create/edit/delete report-email schedules + manual
 * "Run now" button per row. Cadence presets are daily/weekly/monthly with a
 * UTC HH:MM picker. Recipients combine a multi-select from the user
 * directory with a free-text tag input for external addresses.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarClock, Plus, Pencil, Trash2, Play, X, Mail, AlertCircle, CheckCircle2,
  Slack, MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";

const CADENCE_LABEL = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
const WEEKDAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const DEST_META = {
  email: { label: "Email", Icon: Mail, tint: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slack: { label: "Slack", Icon: Slack, tint: "bg-purple-50 text-purple-700 border-purple-200" },
  teams: { label: "Teams", Icon: MessagesSquare, tint: "bg-sky-50 text-sky-700 border-sky-200" },
};

const blank = () => ({
  name: "",
  source_type: "builtin",
  source_id: "volume",
  cadence: "daily",
  hour: 8,
  minute: 0,
  day_of_week: "mon",
  day_of_month: 1,
  destination_type: "email",
  webhook_url: "",
  recipient_user_emails: [],
  recipient_free_emails: [],
  subject: "",
  active: true,
});

function describeWhen(s) {
  const t = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")} UTC`;
  if (s.cadence === "daily") return `Daily at ${t}`;
  if (s.cadence === "weekly") return `Weekly on ${WEEKDAY_LABEL[s.day_of_week] || s.day_of_week} at ${t}`;
  return `Monthly on day ${s.day_of_month} at ${t}`;
}

function fmtIso(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status, error }) {
  if (!status) return <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">never run</Badge>;
  if (status === "sent") return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">sent</Badge>;
  if (status === "skipped") return <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">skipped</Badge>;
  return <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200" title={error || ""}>failed</Badge>;
}

export default function ScheduledReportsTab() {
  const [meta, setMeta] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState({});

  const load = async () => {
    const [m, s, cr, u] = await Promise.all([
      api.get("/report-schedules/builtin-list"),
      api.get("/report-schedules"),
      api.get("/custom-reports").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setMeta(m.data);
    setSchedules(s.data);
    setCustomReports(cr.data);
    setUsers((u.data || []).filter((x) => x.active && x.email));
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(blank()); setDialogOpen(true); };
  const startEdit = (s) => {
    setEditing({
      ...blank(),
      ...s,
      _id_for_edit: s.id,
    });
    setDialogOpen(true);
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try {
      await api.delete(`/report-schedules/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const runNow = async (s) => {
    setRunning((r) => ({ ...r, [s.id]: true }));
    try {
      const { data } = await api.post(`/report-schedules/${s.id}/run-now`);
      if (data.sent) toast.success(`Sent to ${(data.recipients || []).length} recipient(s)`);
      else toast.error(data.error || "Send failed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning((r) => ({ ...r, [s.id]: false }));
    }
  };

  if (!meta) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="scheduled-reports-tab">
      <Card className="border-indigo-100 bg-indigo-50/30">
        <CardContent className="p-4 flex items-start gap-3 text-xs text-indigo-900">
          <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <strong>Scheduled report delivery.</strong> Pick a custom or built-in report and push it to inboxes on a cadence —
            daily, weekly, or monthly. CSV is attached automatically; the email body shows the top rows inline.
            {meta.can_manage
              ? <> You're a manager+, so you can create schedules below.</>
              : <> Only managers and above can create schedules — ask a manager to add yours.</>}
          </div>
          {meta.can_manage && (
            <Button size="sm" onClick={startCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="schedule-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />New schedule
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="schedules-list">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Saved schedules</CardTitle></CardHeader>
        <CardContent className="p-0">
          {schedules.length === 0 ? (
            <div className="text-xs text-slate-400 px-4 py-8 text-center">
              No schedules yet. {meta.can_manage && "Click \u2018New schedule\u2019 to create one."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {schedules.map((s) => {
                const Dest = DEST_META[s.destination_type || "email"] || DEST_META.email;
                const destDescriptor = (s.destination_type === "email" || !s.destination_type)
                  ? `${(s.recipients || []).length} recipient(s)`
                  : (s.webhook_url ? `webhook ${(s.webhook_url || "").slice(0, 28)}…` : "no webhook");
                return (
                <div key={s.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`schedule-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{s.name}</span>
                      <Badge variant="outline" className={`text-[10px] gap-1 ${Dest.tint}`}>
                        <Dest.Icon className="w-2.5 h-2.5" />{Dest.label}
                      </Badge>
                      {!s.active && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">paused</Badge>}
                      <StatusBadge status={s.last_run_status} error={s.last_error} />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {describeWhen(s)} &middot; <span className="text-indigo-600">{s.source_label}</span> &middot; {destDescriptor}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 flex gap-3 flex-wrap">
                      <span>Next: <strong>{fmtIso(s.next_run_at)}</strong></span>
                      <span>Last: <strong>{fmtIso(s.last_run_at)}</strong></span>
                    </div>
                  </div>
                  {meta.can_manage && (
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={running[s.id]} onClick={() => runNow(s)} data-testid={`schedule-run-${s.id}`}>
                        <Play className="w-3 h-3 mr-1" />{running[s.id] ? "Sending…" : "Run now"}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => startEdit(s)} data-testid={`schedule-edit-${s.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[11px] text-rose-600" onClick={() => remove(s)} data-testid={`schedule-delete-${s.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <ScheduleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          meta={meta}
          initial={editing}
          customReports={customReports}
          users={users}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ============== Builder dialog ==============
function ScheduleDialog({ open, onOpenChange, meta, initial, customReports, users, onSaved }) {
  const [form, setForm] = useState(initial || blank());
  const [saving, setSaving] = useState(false);
  const [freeInput, setFreeInput] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleUser = (email) => {
    const set_ = new Set(form.recipient_user_emails);
    if (set_.has(email)) set_.delete(email); else set_.add(email);
    set({ recipient_user_emails: [...set_] });
  };

  const addFree = (e) => {
    if (e) e.preventDefault();
    const val = (freeInput || "").trim().toLowerCase();
    if (!val.includes("@")) { setFreeInput(""); return; }
    if (form.recipient_free_emails.includes(val)) { setFreeInput(""); return; }
    set({ recipient_free_emails: [...form.recipient_free_emails, val] });
    setFreeInput("");
  };
  const removeFree = (e) => set({ recipient_free_emails: form.recipient_free_emails.filter((x) => x !== e) });

  const totalRecipients = useMemo(() =>
    new Set([...(form.recipient_user_emails || []), ...(form.recipient_free_emails || [])]).size,
    [form.recipient_user_emails, form.recipient_free_emails]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.destination_type === "email" && totalRecipients === 0) return toast.error("Add at least one recipient");
    if (form.destination_type !== "email" && !(form.webhook_url || "").trim()) return toast.error("Webhook URL required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        source_type: form.source_type,
        source_id: form.source_id,
        cadence: form.cadence,
        hour: Number(form.hour),
        minute: Number(form.minute),
        day_of_week: form.day_of_week,
        day_of_month: Number(form.day_of_month),
        destination_type: form.destination_type || "email",
        webhook_url: form.webhook_url || "",
        recipient_user_emails: form.recipient_user_emails,
        recipient_free_emails: form.recipient_free_emails,
        subject: form.subject || "",
        active: !!form.active,
      };
      if (form._id_for_edit) {
        await api.patch(`/report-schedules/${form._id_for_edit}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/report-schedules", payload);
        toast.success("Schedule created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-indigo-600" />
            {form._id_for_edit ? "Edit" : "New"} schedule
          </DialogTitle>
          <DialogDescription className="text-xs">
            Times are UTC. CSV of the full result is attached to every email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Name <span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Weekly volume digest" data-testid="schedule-name" />
            </div>
            <div className="space-y-1.5 flex items-end gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} id="schedule-active" data-testid="schedule-active" />
              <Label htmlFor="schedule-active" className="text-xs cursor-pointer">{form.active ? "Active" : "Paused"}</Label>
            </div>
          </div>

          {/* Source */}
          <div className="border-t pt-3 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Report source</Label>
              <Select value={form.source_type} onValueChange={(v) => set({ source_type: v, source_id: v === "builtin" ? "volume" : (customReports[0]?.id || "") })}>
                <SelectTrigger className="text-xs" data-testid="schedule-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="builtin">Built-in report</SelectItem>
                  <SelectItem value="custom_report">Saved custom report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{form.source_type === "builtin" ? "Built-in template" : "Custom report"}</Label>
              <Select value={form.source_id} onValueChange={(v) => set({ source_id: v })}>
                <SelectTrigger className="text-xs" data-testid="schedule-source-id"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>
                  {form.source_type === "builtin"
                    ? meta.builtins.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)
                    : customReports.length === 0
                      ? <div className="px-2 py-1.5 text-[11px] text-slate-400">No custom reports yet.</div>
                      : customReports.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cadence */}
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Cadence</Label>
            <div className="flex flex-wrap items-end gap-2">
              <Select value={form.cadence} onValueChange={(v) => set({ cadence: v })}>
                <SelectTrigger className="text-xs w-28" data-testid="schedule-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.cadences.map((c) => <SelectItem key={c} value={c}>{CADENCE_LABEL[c]}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.cadence === "weekly" && (
                <Select value={form.day_of_week} onValueChange={(v) => set({ day_of_week: v })}>
                  <SelectTrigger className="text-xs w-24" data-testid="schedule-day-of-week"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.weekdays.map((w) => <SelectItem key={w} value={w}>{WEEKDAY_LABEL[w]}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {form.cadence === "monthly" && (
                <div className="flex items-center gap-1">
                  <Label className="text-[10px] text-slate-500">Day</Label>
                  <Input type="number" min={1} max={28} value={form.day_of_month}
                         onChange={(e) => set({ day_of_month: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })}
                         className="text-xs h-9 w-16" data-testid="schedule-day-of-month" />
                </div>
              )}
              <div className="flex items-center gap-1">
                <Label className="text-[10px] text-slate-500">at</Label>
                <Input type="number" min={0} max={23} value={form.hour}
                       onChange={(e) => set({ hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                       className="text-xs h-9 w-14" data-testid="schedule-hour" />
                <span className="text-slate-500">:</span>
                <Input type="number" min={0} max={59} value={form.minute}
                       onChange={(e) => set({ minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })}
                       className="text-xs h-9 w-14" data-testid="schedule-minute" />
                <span className="text-[10px] text-slate-400">UTC</span>
              </div>
            </div>
          </div>

          {/* Destination */}
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Send to</Label>
            <div className="grid grid-cols-3 gap-2">
              {(meta.destinations || [{key: "email", label: "Email"}]).map((d) => {
                const DestIcon = (DEST_META[d.key] || {}).Icon || Mail;
                const active = form.destination_type === d.key;
                return (
                  <button key={d.key} type="button"
                          onClick={() => set({ destination_type: d.key })}
                          className={`border rounded-md px-3 py-2 text-left transition ${active ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                          data-testid={`schedule-dest-${d.key}`}>
                    <div className="flex items-center gap-1.5">
                      <DestIcon className={`w-3.5 h-3.5 ${active ? "text-indigo-600" : "text-slate-500"}`} />
                      <span className={`text-sm font-medium ${active ? "text-indigo-700" : "text-slate-700"}`}>{d.label}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {d.needs_recipients ? "Email list" : "Webhook URL"}
                    </div>
                  </button>
                );
              })}
            </div>
            {form.destination_type !== "email" && (
              <div className="space-y-1.5">
                <Label className="text-[10px] text-slate-500">Incoming webhook URL <span className="text-rose-500">*</span></Label>
                <Input value={form.webhook_url || ""} onChange={(e) => set({ webhook_url: e.target.value })}
                       placeholder={form.destination_type === "slack" ? "https://hooks.slack.com/services/T0…/B0…/XYZ" : "https://kippu.webhook.office.com/webhookb2/…"}
                       className="text-xs font-mono"
                       data-testid="schedule-webhook-url" />
                <p className="text-[10px] text-slate-400">
                  {(meta.destinations || []).find((d) => d.key === form.destination_type)?.help || ""}
                </p>
              </div>
            )}
          </div>

          {/* Recipients (only for email destination) */}
          {form.destination_type === "email" && (
          <div className="border-t pt-3 space-y-2" data-testid="schedule-recipients-block">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Recipients</Label>
              <span className="text-[10px] text-slate-400">{totalRecipients} selected</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(form.recipient_user_emails || []).map((e) => (
                <Badge key={e} variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 gap-1">
                  {e}
                  <button type="button" onClick={() => toggleUser(e)} className="hover:text-rose-600"><X className="w-2.5 h-2.5" /></button>
                </Badge>
              ))}
              {(form.recipient_free_emails || []).map((e) => (
                <Badge key={e} variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                  {e}
                  <button type="button" onClick={() => removeFree(e)} className="hover:text-rose-600"><X className="w-2.5 h-2.5" /></button>
                </Badge>
              ))}
              {totalRecipients === 0 && (
                <span className="text-[10px] text-slate-400 italic">Pick from team below or add a free-text email.</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[10px] text-slate-500">Pick team members</Label>
                <Select onValueChange={(v) => toggleUser(v)}>
                  <SelectTrigger className="text-xs h-9" data-testid="schedule-pick-user"><SelectValue placeholder="Add a teammate…" /></SelectTrigger>
                  <SelectContent>
                    {users.length === 0
                      ? <div className="px-2 py-1.5 text-[11px] text-slate-400">No users.</div>
                      : users.slice(0, 200).map((u) => {
                          const picked = form.recipient_user_emails.includes(u.email);
                          return <SelectItem key={u.email} value={u.email}>{picked ? "✓ " : ""}{u.name || u.email} <span className="text-slate-400">&nbsp;{u.email}</span></SelectItem>;
                        })}
                  </SelectContent>
                </Select>
              </div>
              <form onSubmit={addFree} className="flex gap-1 items-end">
                <div>
                  <Label className="text-[10px] text-slate-500">…or a free-text email</Label>
                  <Input value={freeInput} onChange={(e) => setFreeInput(e.target.value)} placeholder="ops@partner.com" className="text-xs h-9 w-52" data-testid="schedule-free-email" />
                </div>
                <Button type="submit" size="sm" variant="outline" className="h-9 text-xs" data-testid="schedule-add-free-email">Add</Button>
              </form>
            </div>
          </div>
          )}

          {/* Subject (optional, email-only) */}
          {form.destination_type === "email" && (
          <div className="border-t pt-3 space-y-1.5">
            <Label className="text-xs">Email subject <span className="text-slate-400 font-normal">(optional override)</span></Label>
            <Input value={form.subject || ""} onChange={(e) => set({ subject: e.target.value })} placeholder={`Kippu scheduled report — ${form.name || "<name>"}`} className="text-xs" />
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="schedule-save-btn">
            {saving ? "Saving…" : (form._id_for_edit ? "Save changes" : "Create schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
